import api from '@/lib/api';

export interface PushRatePayload {
  unitId: string;
  dates: Array<{ from: string; to: string }>;
  rate: number;
  channel?: string;
}

export interface ChannelStatusQuery {
  propertyId?: string;
}

export interface SyncLogQuery {
  propertyId?: string;
  page?: number;
  limit?: number;
}

export async function fetchChannelStatus(query?: ChannelStatusQuery) {
  const { data } = await api.get('/channels/status', { params: query });
  return data;
}

export async function syncChannel(unitId: string, channel?: string) {
  const { data } = await api.post('/channels/sync', { unitId, channel });
  return data;
}

/** Push availability for EVERY mapped room on a connection (staff "Sync Now"). */
export async function syncConnection(connectionId: string) {
  const { data } = await api.post(`/channels/connections/${connectionId}/sync`, {});
  return data;
}

export async function pushRate(payload: PushRatePayload) {
  const { data } = await api.post('/channels/rates/push', payload);
  return data;
}

export async function fetchSyncLog(query?: SyncLogQuery) {
  const { data } = await api.get('/channels/sync-log', { params: query });
  return data;
}

/** Manual "Push to channels" — flush a unit's pending push immediately. */
export async function pushToChannels(unitId: string, channel?: string) {
  const { data } = await api.post('/channels/push', { unitId, channel });
  return data;
}

/** Replay a previously-failed availability/rate sync-log entry. */
export async function retrySyncLog(id: string) {
  const { data } = await api.post(`/channels/sync-log/${id}/retry`, {});
  return data;
}

// ---- Connection CRUD ----

export async function createConnection(payload: {
  propertyId: string;
  channel: string;
  externalPropertyId: string;
  credentials?: Record<string, unknown> | undefined;
  sandboxMode?: boolean;
  /** Shared physical-room pool size — set only for shared-pool properties. */
  poolSize?: number;
}) {
  const { data } = await api.post('/channels/connections', payload);
  return data;
}

export interface ConnectionDetail {
  id: string;
  channel: string;
  externalPropertyId: string;
  isActive: boolean;
  sandboxMode: boolean;
  /** Present only for AxisRooms — the editable, non-secret config. */
  axisRooms?: { hotelId: string; ratePlanIds: string[] };
}

/** GET /channels/connections/:id — non-secret detail for the edit form. */
export async function getConnection(id: string): Promise<ConnectionDetail> {
  const { data } = await api.get<ConnectionDetail>(`/channels/connections/${id}`);
  return data;
}

export async function updateConnection(
  id: string,
  payload: {
    externalPropertyId?: string;
    credentials?: Record<string, unknown> | undefined;
    isActive?: boolean;
    sandboxMode?: boolean;
    /** Positive int to enable shared-pool-with-caps; null to clear it. */
    poolSize?: number | null;
  },
) {
  const { data } = await api.put(`/channels/connections/${id}`, payload);
  return data;
}

export async function deleteConnection(id: string) {
  await api.delete(`/channels/connections/${id}`);
}

export async function testConnection(id: string) {
  const { data } = await api.post(`/channels/connections/${id}/test`);
  return data;
}

// ---- Unit Mappings ----

export async function fetchUnitMappings(connectionId: string) {
  const { data } = await api.get(`/channels/connections/${connectionId}/mappings`);
  return data;
}

export async function createUnitMapping(
  connectionId: string,
  payload: { unitId: string; externalListingId: string; allotment?: number },
) {
  const { data } = await api.post(`/channels/connections/${connectionId}/mappings`, payload);
  return data;
}

export async function deleteUnitMapping(connectionId: string, mappingId: string) {
  await api.delete(`/channels/connections/${connectionId}/mappings/${mappingId}`);
}

// ---- OAuth ----

export async function getAirbnbOAuthUrl() {
  const { data } = await api.get('/channels/oauth/airbnb/authorize');
  return data;
}

// ─── Sync Schedules ─────────────────────────────────────────────────────────

export interface SyncSchedule {
  id: string;
  propertyId: string;
  channel: string;
  syncType: string;
  intervalMinutes: number;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastSyncStatus: string | null;
  consecutiveFailures: number;
}

export interface CreateSyncSchedulePayload {
  propertyId: string;
  channel: string;
  syncType: string;
  intervalMinutes: number;
}

export interface UpdateSyncSchedulePayload {
  intervalMinutes?: number;
  isActive?: boolean;
}

export async function fetchSyncSchedules(): Promise<SyncSchedule[]> {
  const { data } = await api.get<SyncSchedule[]>('/channels/schedules');
  return data;
}

export async function createSyncSchedule(
  payload: CreateSyncSchedulePayload,
): Promise<SyncSchedule> {
  const { data } = await api.post<SyncSchedule>('/channels/schedules', payload);
  return data;
}

export async function updateSyncSchedule(
  id: string,
  payload: UpdateSyncSchedulePayload,
): Promise<SyncSchedule> {
  const { data } = await api.patch<SyncSchedule>(
    `/channels/schedules/${id}`,
    payload,
  );
  return data;
}

export async function deleteSyncSchedule(id: string): Promise<void> {
  await api.delete(`/channels/schedules/${id}`);
}

export async function triggerSyncSchedule(id: string): Promise<SyncSchedule> {
  const { data } = await api.post<SyncSchedule>(
    `/channels/schedules/${id}/run`,
  );
  return data;
}
